import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { StartPricesList, startPricesListSchema } from '../../../types/StartPrices';
import request from '../request';
import queryKeys from '../reactQuery/queryKeys';

const getPropertyIdsQuery = (propertyIds?: (string | undefined)[] | string) => {
  if (typeof propertyIds === 'string') {
    return `property_ids=${propertyIds}`;
  }
  return propertyIds?.map((propertyId) => `property_ids=${propertyId}`).join('&');
};

const fetchData = async (
  {
    propertyIds,
    date,
    locale,
  }: {
    propertyIds?: (string | undefined)[] | string;
    date?: string;
    locale?: string;
  },
  signal?: AbortSignal
) => {
  const propertyIdsQuery = getPropertyIdsQuery(propertyIds);
  const searchParams = `?${propertyIdsQuery}&date=${date}&locale=${locale}&tabist=ok`;

  const data = await request<StartPricesList>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/hotels/start-prices${searchParams}`,
    method: 'GET',
    signal,
  });

  try {
    // Validate the response in case the API returns unexpected data such as undefined
    const validatedData = startPricesListSchema.validateSync(data);
    return validatedData;
  } catch (err: unknown) {
    console.error(err);
    reportError(err);
    throw new Error('Invalid data returned from API');
  }
};

const useStartPrices = ({
  propertyIds,
  date,
  locale,
}: {
  propertyIds?: (string | undefined)[] | string;
  date?: string;
  locale?: string;
}) => {
  const res = useQuery<StartPricesList, Error>({
    queryKey: queryKeys.startPrices(propertyIds, date, locale),
    queryFn: ({ signal }) => fetchData({ propertyIds, date, locale }, signal),
    keepPreviousData: true,
    enabled: !!propertyIds && !!date && moment(date).isValid(),
  });

  return {
    startPricesList: res.data,
    ...res,
  };
};
export default useStartPrices;
